import { useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import { useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import SafeArea from "components/safe-area";
import FlatContainer from "components/container/flat";
import Table from "components/table";
import { OutlineButton } from "components/button";
import { Status } from "components/status";
import { CampaignReportSummary, CampaignReportEntriesPage, ReportStatusFilter } from "../types/ICampaign";
import {
  getCampaignReportSummary,
  getCampaignReportEntries,
  downloadCampaignReportCsv,
  createFollowUp,
  REPORT_PAGE_SIZE,
} from "../api/campaigns";

type StatusFilter = ReportStatusFilter;

/**
 * The statuses a follow-up campaign may target.
 *
 * Bounced and complained are deliberately absent. Re-mailing an address that
 * bounced is what drives a sender reputation down, and mailing someone who
 * reported the message as spam is worse than useless — both are the opposite
 * of what the suppression work exists to prevent. The API accepts only these
 * four for the same reason.
 */
type FollowUpStatus = "sent" | "failed" | "opened" | "not_opened";

const FOLLOW_UP_STATUSES: FollowUpStatus[] = [
  "sent",
  "failed",
  "opened",
  "not_opened",
];

const canFollowUp = (f: StatusFilter): f is FollowUpStatus =>
  (FOLLOW_UP_STATUSES as string[]).includes(f);

// "Opened" / "Not opened" use the delivered rule (sent or complained) — now
// applied by the API in SQL so the dropdown and the header can never disagree.

const STATUS_COLORS: Record<string, string> = {
  sent: "0,200,100",
  failed: "239,68,68",
  queued: "234,179,8",
  bounced: "249,115,22",
  complained: "168,85,247",
  pending: "160,160,160",
};

const CampaignReportPage = () => {
  const { t } = useTranslation();
  const tBase = "views.outreach.campaigns.report";
  const tr = (key: string) => t(`${tBase}.${key}`);

  const { id } = useParams<{ id: string }>();
  const history = useHistory();

  // All useState hooks must be BEFORE any early returns (Rules of Hooks)
  const [statusFilter, setStatusFilterState] = useState<StatusFilter>("all");
  const [page, setPage] = useState(1);
  const setStatusFilter = (f: StatusFilter) => {
    setStatusFilterState(f);
    setPage(1);
  };

  // Header figures + dropdown counts: one light request. The rows come one
  // page at a time below — the old single request carried every send-log row
  // (23,715 / 7.5 MB for Millard's campaign 63) and rendering them froze the
  // page for seconds per interaction (ESERV2-1457).
  const {
    data: report,
    isLoading,
    isError,
  } = useQuery<CampaignReportSummary>(["campaign-report-summary", id], () =>
    getCampaignReportSummary(id)
  );
  const { data: entriesPage, isFetching: isFetchingEntries } = useQuery<CampaignReportEntriesPage>(
    ["campaign-report-entries", id, statusFilter, page],
    () => getCampaignReportEntries(id, statusFilter, page, REPORT_PAGE_SIZE),
    { keepPreviousData: true }
  );

  const [isFollowingUp, setIsFollowingUp] = useState(false);

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return "--";
    }
    const date = new Date(dateString);
    return date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const [isExporting, setIsExporting] = useState(false);
  const handleExportCsv = async () => {
    setIsExporting(true);
    try {
      await downloadCampaignReportCsv(id, statusFilter);
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-gray-600 text-lg">{tr("loading")}</p>
        </div>
      </SafeArea>
    );
  }

  if (isError || !report) {
    return (
      <SafeArea>
        <div className="py-12 flex items-center justify-center">
          <p className="text-red-500 text-lg">{tr("loadError")}</p>
        </div>
      </SafeArea>
    );
  }

  const handleFollowUp = async () => {
    if (!canFollowUp(statusFilter)) return;
    setIsFollowingUp(true);
    try {
      const followUp = await createFollowUp(id, statusFilter);
      history.push(`/outreach/campaigns/${followUp.id}/edit`);
    } catch {
      setIsFollowingUp(false);
    }
  };

  const { campaign, tracking, summary, unsubscribed, counts } = report;
  // One page of the delivery log, filtered by the API
  const entries = entriesPage?.rows ?? [];
  const filteredTotal = entriesPage?.total ?? counts[statusFilter];
  const pageCount = entriesPage?.pageCount ?? 1;
  // Counted from the send log, the same rows the delivery table below lists.
  // The campaign's own totalSent/totalFailed are frozen when the send finishes,
  // so a delivery result that arrives later — Twilio reporting an undeliverable
  // number — left this header claiming 100% sent above a row marked Failed.
  const totalRecipients = summary.total;
  const totalSent = summary.sent;
  const totalFailed = summary.failed;
  const totalQueued = summary.queued;
  const totalBounced = summary.bounced;
  const totalComplained = summary.complained;
  const pctOf = (n: number) =>
    totalRecipients > 0 ? Math.round((n / totalRecipients) * 100) : 0;
  const sentPct = pctOf(totalSent);
  const failedPct = pctOf(totalFailed);
  const queuedPct = pctOf(totalQueued);
  const bouncedPct = pctOf(totalBounced);
  const complainedPct = pctOf(totalComplained);
  // Open and click rates are shares of what actually reached an inbox. A
  // complaint is raised from inside the mail client, so those were delivered
  // and belong in the denominator; a bounce never arrived and does not.
  // Dividing by `sent` alone let the rate exceed 100% once a delivered row
  // moved to 'complained' while its opens stayed counted.
  const delivered = totalSent + totalComplained;
  // Of those who received it — the rate that answers "did this campaign cost
  // us subscribers", which a share of everyone we tried to reach would not.
  const unsubscribedPct =
    delivered > 0 ? Math.round(((unsubscribed ?? 0) / delivered) * 100) : 0;
  const openPct =
    delivered > 0
      ? Math.round(((tracking?.uniqueOpens ?? 0) / delivered) * 100)
      : 0;
  const clickPct =
    delivered > 0
      ? Math.round(((tracking?.uniqueClicks ?? 0) / delivered) * 100)
      : 0;

  const logColumns = {
    recipientAddress: {
      label: campaign.campaignType === "sms" ? tr("colPhone") : tr("colEmail"),
      cell: (row: (typeof entries)[0]) => row.recipientAddress,
    },
    status: {
      label: tr("colStatus"),
      cell: (row: (typeof entries)[0]) => (
        <Status color={STATUS_COLORS[row.status] || "160,160,160"}>
          {row.status}
        </Status>
      ),
    },
    sentAt: {
      label: tr("colSentAt"),
      cell: (row: (typeof entries)[0]) => formatDate(row.sentAt),
    },
    errorMessage: {
      label: tr("colError"),
      cell: (row: (typeof entries)[0]) => (
        <span className="text-red-500 text-sm">
          {row.errorMessage || "--"}
        </span>
      ),
    },
    ...(campaign.campaignType === "email"
      ? {
          openedAt: {
            label: tr("colOpenedAt"),
            cell: (row: (typeof entries)[0]) => formatDate(row.openedAt),
          },
        }
      : {}),
    clickCount: {
      label: tr("colClicks"),
      cell: (row: (typeof entries)[0]) => row.clickCount || 0,
    },
  };

  return (
    <SafeArea>
      {/* Header */}
      <FlatContainer
        title={<h2>{tr("title")}</h2>}
        buttons={[
          <OutlineButton
            key="back"
            size="small"
            onClick={() => history.push("/outreach/campaigns")}
          >
            {tr("backToList")}
          </OutlineButton>,
        ]}
      />

      {/* Campaign Details */}
      <div className="py-4 border-b">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <div>
            <span className="text-sm text-gray-500">{tr("campaignName")}</span>
            <p className="font-semibold">{campaign.name}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">
              {campaign.campaignType === "sms" ? tr("message") : tr("subject")}
            </span>
            <p className="font-semibold">
              {campaign.campaignType === "sms"
                ? (campaign.messageText || "--")
                : (campaign.subject || "--")}
            </p>
          </div>
          <div>
            <span className="text-sm text-gray-500">{tr("sentDate")}</span>
            <p className="font-semibold">{formatDate(campaign.sentAt)}</p>
          </div>
          <div>
            <span className="text-sm text-gray-500">{tr("sentBy")}</span>
            <p className="font-semibold">
              {campaign.sentByName || campaign.sentBy || "--"}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="py-4 border-b">
        <h3 className="text-lg font-bold mb-4">{tr("summary")}</h3>
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-6">
          {/* Total */}
          <div className="text-center">
            <p className="text-3xl font-bold">{totalRecipients}</p>
            <p className="text-sm text-gray-500">{tr("total")}</p>
          </div>
          {/* Sent */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-green-700">
                {tr("sent")}
              </span>
              <span className="text-sm font-medium text-green-700">
                {totalSent} ({sentPct}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-green-500 h-3 rounded-full"
                style={{ width: `${sentPct}%` }}
              />
            </div>
          </div>
          {/* Failed */}
          <div>
            <div className="flex justify-between mb-1">
              <span className="text-sm font-medium text-red-700">
                {tr("failed")}
              </span>
              <span className="text-sm font-medium text-red-700">
                {totalFailed} ({failedPct}%)
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-3">
              <div
                className="bg-red-500 h-3 rounded-full"
                style={{ width: `${failedPct}%` }}
              />
            </div>
          </div>
          {/* Queued — email only: sms sends complete in-process, so the
              queued bucket can never be meaningfully non-zero there */}
          {campaign.campaignType === "email" && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-yellow-700">
                  {tr("queued")}
                </span>
                <span className="text-sm font-medium text-yellow-700">
                  {totalQueued > 0 ? totalQueued : 0} ({queuedPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-yellow-500 h-3 rounded-full"
                  style={{ width: `${queuedPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Bounced — email only: a bounce is an SES/provider delivery
              result, and it must be visible or the Sent figure reads as
              delivery when part of it never arrived */}
          {campaign.campaignType === "email" && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-orange-700">
                  {tr("bounced")}
                </span>
                <span className="text-sm font-medium text-orange-700">
                  {totalBounced} ({bouncedPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-orange-500 h-3 rounded-full"
                  style={{ width: `${bouncedPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Complained — shown only when there is one. Complaints are rare
              and a permanently-zero card would crowd the row, but a single one
              matters enough to surface */}
          {campaign.campaignType === "email" && totalComplained > 0 && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-purple-700">
                  {tr("complained")}
                </span>
                <span className="text-sm font-medium text-purple-700">
                  {totalComplained} ({complainedPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-500 h-3 rounded-full"
                  style={{ width: `${complainedPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Unsubscribed — email only, and only when there are any. This is
              not another delivery bucket: someone counted as sent can also
              have unsubscribed afterwards, so it deliberately sits apart from
              the figures that must sum to the total */}
          {campaign.campaignType === "email" && (unsubscribed ?? 0) > 0 && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-rose-700">
                  {tr("unsubscribed")}
                </span>
                <span className="text-sm font-medium text-rose-700">
                  {unsubscribed} ({unsubscribedPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-rose-500 h-3 rounded-full"
                  style={{ width: `${unsubscribedPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Opened (email only) */}
          {campaign.campaignType === "email" && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-blue-700">
                  {tr("opened")}
                </span>
                <span className="text-sm font-medium text-blue-700">
                  {tracking?.uniqueOpens ?? 0} ({openPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-blue-500 h-3 rounded-full"
                  style={{ width: `${openPct}%` }}
                />
              </div>
            </div>
          )}
          {/* Clicked — click tracking only exists when the message carries a
              link (sms links are wrapped in tracking URLs); a linkless sms
              can only ever show 0, so hide the card there */}
          {(campaign.campaignType === "email" ||
            /https?:\/\//i.test(campaign.messageText || "")) && (
            <div>
              <div className="flex justify-between mb-1">
                <span className="text-sm font-medium text-purple-700">
                  {tr("clicked")}
                </span>
                <span className="text-sm font-medium text-purple-700">
                  {tracking?.uniqueClicks ?? 0} ({clickPct}%)
                </span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-3">
                <div
                  className="bg-purple-500 h-3 rounded-full"
                  style={{ width: `${clickPct}%` }}
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Delivery Log */}
      <div className="py-4">
        <FlatContainer
          title={<h3 className="text-lg font-bold">{tr("deliveryLog")}</h3>}
          buttons={[
            canFollowUp(statusFilter) && (
              <OutlineButton
                key="follow-up"
                size="small"
                onClick={handleFollowUp}
                disabled={isFollowingUp || filteredTotal === 0}
              >
                {isFollowingUp
                  ? "Creating..."
                  : `Send follow-up to ${filteredTotal} recipients`}
              </OutlineButton>
            ),
            <OutlineButton
              key="export"
              size="small"
              onClick={handleExportCsv}
              disabled={isExporting}
            >
              {isExporting ? "Exporting..." : tr("exportCsv")}
            </OutlineButton>,
          ].filter(Boolean)}
        >
          {/* Status Filter */}
          <div className="flex items-center gap-3 mb-4 pb-4 border-b">
            <label className="text-sm text-gray-500 font-medium">Filter by status:</label>
            <select
              className="border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:border-brand"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
            >
              <option value="all">All ({counts.all})</option>
              <option value="sent">Sent ({counts.sent})</option>
              <option value="failed">Failed ({counts.failed})</option>
              {campaign.campaignType === "email" && (
                <>
                  <option value="bounced">Bounced ({counts.bounced})</option>
                  {counts.complained > 0 && (
                    <option value="complained">Complained ({counts.complained})</option>
                  )}
                </>
              )}
              {campaign.campaignType === "email" && (
                <>
                  <option value="opened">Opened ({counts.opened})</option>
                  <option value="not_opened">Not Opened ({counts.not_opened})</option>
                </>
              )}
            </select>
            <span className="text-sm text-gray-400">
              {statusFilter !== "all" ? `Showing ${filteredTotal} of ${counts.all}` : `${counts.all} rows`}
              {pageCount > 1 ? ` · page ${page} of ${pageCount}` : ""}
              {isFetchingEntries ? " · loading…" : ""}
            </span>
          </div>

          <Table
            data={entries}
            columns={logColumns}
            disableSortAll
            pageCount={pageCount}
            pageIndex={page}
            handlePage={(p: number) => setPage(p)}
          />
        </FlatContainer>
      </div>
    </SafeArea>
  );
};

export default CampaignReportPage;
